import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { type CSSProperties, type MouseEvent, type ReactNode, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import { Flex } from '@/components/base';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';

interface LabelProps {
  text: ReactNode;
  themeColor: number;
  isSelected?: boolean;
  isFilter?: boolean;
  cancellable?: boolean;
  withoutBorder?: boolean;
  maxWidth?: number;
  focused?: boolean;
  onClick?: (e: MouseEvent) => void;
  onDeleteClick?: () => void;
  style?: CSSProperties;
  customColor?: string;
  withoutHover?: boolean;
  readOnly?: boolean;
}

const Label = ({
  text,
  themeColor,
  isSelected,
  isFilter,
  cancellable,
  style,
  maxWidth,
  focused,
  customColor,
  withoutBorder,
  withoutHover,
  onClick,
  onDeleteClick,
  readOnly,
}: LabelProps) => {
  const color = useColor();

  const focusedRef = useRef<HTMLDivElement>(null);
  const dummyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusedRef?.current) {
      const target = focusedRef.current as any;
      target?.scrollIntoViewIfNeeded?.({
        block: 'end',
        inline: 'nearest',
      });
    }
  }, [focusedRef, focused]);

  return (
    <LabelWrapper
      ref={focused ? focusedRef : dummyRef}
      onClick={onClick}
      d="row"
      a="center"
      g="8px"
      j="space-between"
      selected={isSelected}
      focused={focused}
      isFilter={isFilter}
      style={style}
      withoutBorder={withoutBorder}
      withoutHover={withoutHover}
      autoFocus={focused}
      readOnly={readOnly}
    >
      <div
        className="color-circle"
        style={{
          backgroundColor: customColor
            ? customColor
            : color.profile[themeColor - 1 || 0],
        }}
      ></div>
      <LabelText
        color={isSelected ? color.gray800 : color.gray700}
        style={{ maxWidth: maxWidth || 'auto' }}
      >
        {text}
        {isSelected && <CheckOutlined style={{ marginLeft: 8 }} />}
      </LabelText>
      {cancellable && (
        <DeleteButton onClick={onDeleteClick}>
          <CloseOutlined style={{ color: color.gray600, fontSize: '12px' }} />
        </DeleteButton>
      )}
    </LabelWrapper>
  );
};

type StyleProps = {
  selected?: boolean;
  isFilter?: boolean;
  focused?: boolean;
  withoutBorder?: boolean;
  withoutHover?: boolean;
  readonly?: boolean;
};

export const LabelWrapper = styled(Flex)<StyleProps>`
  padding: 2px 12px;
  cursor: ${(props) => (props.readOnly ? 'default' : 'pointer')};
  border-radius: 16px;
  background-color: ${(props) => props.theme.colors.ND00};
  transition: all 0.2s;
  border: 1px solid ${(props) => props.theme.colors.gray300};

  ${({ withoutHover }) =>
    !withoutHover &&
    css`
      &:hover {
        background-color: ${(props) => props.theme.colors.ND04};
      }
    `}

  ${({ isFilter }) =>
    isFilter &&
    css`
      background-color: ${(props) => props.theme.colors.gray200};
      border-color: ${(props) => props.theme.colors.gray600};
    `}
  ${({ selected }) =>
    selected &&
    css`
      background-color: ${(props) => props.theme.colors.gray200};
      border-color: ${(props) => props.theme.colors.gray600};
    `}
  ${({ focused }) =>
    focused &&
    css`
      background-color: ${(props) => props.theme.colors.gray300};
      border-color: ${(props) => props.theme.colors.gray400};
    `}
  ${({ withoutBorder }) =>
    withoutBorder &&
    css`
      border: none;
    `}

  .color-circle {
    width: 6px;
    height: 6px;
    border-radius: 3px;
  }
`;

const DeleteButton = styled.div``;

export const LabelText = styled(Typo).attrs({
  weight: 'r',
  type: 'label3',
})`
  user-select: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default Label;
